/**
 * PRD §8.4 Tier A step 4: strip lines repeating at the same y-band on ≥60%
 * of pages, plus standalone page numbers. Two repetition rules, both keyed
 * on a quantized baseline band:
 *
 *  - text-key: identical digit-stripped text at the same band (catches
 *    "5/18/26 · filename" headers and "about:blank 1/5" footers whose only
 *    variation is numeric);
 *  - extreme-band: the topmost/bottommost band of ≥60% of pages, when its
 *    lines are short relative to the body (catches verso/recto alternating
 *    running headers, where the text differs page to page).
 *
 * The short-line guard keeps body text safe: on pages without ornaments the
 * topmost band is the first body line, which is near full measure.
 */
const REPEAT_PAGE_FRACTION = 0.6;
const MIN_REPEAT_PAGES = 2;
/** Baseline y quantization for band identity across pages. */
const BAND_QUANTUM = 3;
/** Extreme-band lines must be shorter than this fraction of the body measure
 * (p90 line width — median would be dragged down by short/math lines). */
const HEADER_WIDTH_FRACTION = 0.75;
const BODY_MEASURE_PERCENTILE = 0.9;
const STANDALONE_PAGE_NUMBER = /^\s*(?:[-–—]\s*)?[([]?\d{1,4}[)\]]?(?:\s*[-–—])?\s*$/;
/** Front-matter folios: a strict roman numeral, ≥2 chars ("vii", "xii") so
 * prose one-letter lines ("I") never match. */
const STANDALONE_ROMAN_FOLIO = /^\s*(?=[ivxlcdm]{2,7}\s*$)(m{0,3})(cm|cd|d?c{0,3})(xc|xl|l?x{0,3})(ix|iv|v?i{0,3})\s*$/i;
function isStandalonePageNumber(text) {
    return STANDALONE_PAGE_NUMBER.test(text) || STANDALONE_ROMAN_FOLIO.test(text);
}
function band(line) {
    return Math.round(line.y / BAND_QUANTUM);
}
function textKey(line) {
    return `${band(line)}|${line.text.replace(/\d+/g, '#').replace(/\s+/g, ' ').trim().toLowerCase()}`;
}
function percentile(values, fraction) {
    if (values.length === 0)
        return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const index = Math.min(sorted.length - 1, Math.floor(fraction * sorted.length));
    return sorted[index];
}
/**
 * Returns a predicate: true = keep the line, false = strip it.
 * `pages` must hold the assembled lines of every page of the document —
 * the ≥60% statistic is defined over the whole document (PRD §8.4).
 */
export function buildKeepFilter(pages) {
    const pageCount = pages.length;
    const threshold = Math.max(MIN_REPEAT_PAGES, Math.ceil(REPEAT_PAGE_FRACTION * pageCount));
    // Rule 1: same digit-stripped text at the same band across pages.
    const keyPages = new Map();
    for (const lines of pages) {
        for (const line of lines) {
            const key = textKey(line);
            let set = keyPages.get(key);
            if (set === undefined)
                keyPages.set(key, (set = new Set()));
            set.add(line.page);
        }
    }
    const strippedKeys = new Set();
    for (const [key, set] of keyPages)
        if (set.size >= threshold)
            strippedKeys.add(key);
    // Rule 2: extreme bands. Collect each page's topmost and bottommost band.
    const topBandPages = new Map();
    const bottomBandPages = new Map();
    const widthsByBand = new Map();
    for (const lines of pages) {
        if (lines.length === 0)
            continue;
        let top = lines[0];
        let bottom = lines[0];
        for (const line of lines) {
            if (line.y > top.y)
                top = line;
            if (line.y < bottom.y)
                bottom = line;
        }
        for (const [map, extreme] of [
            [topBandPages, top],
            [bottomBandPages, bottom],
        ]) {
            let set = map.get(band(extreme));
            if (set === undefined)
                map.set(band(extreme), (set = new Set()));
            set.add(extreme.page);
        }
        for (const line of lines) {
            let widths = widthsByBand.get(band(line));
            if (widths === undefined)
                widthsByBand.set(band(line), (widths = []));
            widths.push(line.x1 - line.x0);
        }
    }
    const strippedBands = new Set();
    for (const map of [topBandPages, bottomBandPages]) {
        for (const [bandKey, pageSet] of map) {
            if (pageSet.size < threshold)
                continue;
            const others = [];
            for (const [otherBand, widths] of widthsByBand) {
                if (otherBand !== bandKey)
                    others.push(...widths);
            }
            const bandWidth = percentile(widthsByBand.get(bandKey) ?? [], 0.5);
            const bodyMeasure = percentile(others, BODY_MEASURE_PERCENTILE);
            if (bodyMeasure > 0 && bandWidth < HEADER_WIDTH_FRACTION * bodyMeasure) {
                strippedBands.add(bandKey);
            }
        }
    }
    // Rule 3: standalone page numbers at a page's extremes.
    const extremeLines = new Set();
    for (const lines of pages) {
        if (lines.length === 0)
            continue;
        let top = lines[0];
        let bottom = lines[0];
        for (const line of lines) {
            if (line.y > top.y)
                top = line;
            if (line.y < bottom.y)
                bottom = line;
        }
        extremeLines.add(top);
        extremeLines.add(bottom);
    }
    return (line) => {
        if (strippedKeys.has(textKey(line)))
            return false;
        if (strippedBands.has(band(line)))
            return false;
        if (extremeLines.has(line) && isStandalonePageNumber(line.text))
            return false;
        return true;
    };
}
/** Page-local subset of the strip rules, for provisional progressive output. */
export function keepLinePageLocal(line, pageLines) {
    if (pageLines.length === 0)
        return true;
    let top = pageLines[0];
    let bottom = pageLines[0];
    for (const other of pageLines) {
        if (other.y > top.y)
            top = other;
        if (other.y < bottom.y)
            bottom = other;
    }
    if ((line === top || line === bottom) && isStandalonePageNumber(line.text))
        return false;
    return true;
}
//# sourceMappingURL=strip.js.map